import { Entity, FirmCosts } from "../Analytics.ts";
import { CostType, DisbursementType, Stage } from "../CaseConfiguration.ts";

/**
 * The provenance ledger: per (party, outcome), two item families forming a
 * DAG.
 *
 *   - `OriginItem` — facts that cause money to move. Origins carry NO money
 *     movement themselves: a work item is not yet a payment, a costs order
 *     is not yet cash.
 *   - `PaymentItem` — every movement (or absorption) of money. Each payment
 *     states what caused it (`causedBy`): the origins behind it, the
 *     upstream payments it derives from, and the structured parameters of
 *     the derivation.
 *
 * The flat fold is the model's invariant: an entity's net result is the sum
 * of payment amounts signed by its payer/payee role, the payer paying a
 * payment's VAT on top. The `causedBy` edges
 * are annotations — never summed — so no double counting is possible by
 * construction.
 *
 * Stored discriminators follow one razor: a stored field must not be
 * derivable from (stage, payer/payee, causedBy). Hence only four mechanisms
 * are stored — "recharge", "enforcement cost", "judgment debt" and their kin
 * are derived display labels, not kinds. The same razor bans stored prose:
 * derivations hold numbers; labels are rendered via i18n at view time.
 */

// ---- entity references ------------------------------------------------------

/**
 * A per-party-ledger actor. `external` is anything outside the modelled
 * entities (courts, experts, suppliers — and the sink of absorptions).
 * `opponent:<partyId>` references an opposing case party by id; display
 * names are resolved at projection via `LedgerItems.context.partyNames`.
 */
export type EntityRef = Entity | "external" | `opponent:${string}`;

export const opponentRef = (partyId: string): EntityRef =>
  `opponent:${partyId}`;

/** `opponent:<id>` → `<id>`; undefined for every other ref. */
export function opponentIdOf(ref: EntityRef): string | undefined {
  return ref.startsWith("opponent:")
    ? ref.slice("opponent:".length)
    : undefined;
}

/** The fold entity behind a ref — undefined for external/opponent refs. */
export function entityOf(ref: EntityRef): Entity | undefined {
  return ref === "party" || ref === "lawFirm" || ref === "barrister"
    ? ref
    : undefined;
}

// ---- origin items -----------------------------------------------------------

export type OriginId = string;
export type PaymentId = string;

/** A budget cost item — the work/disbursement behind every bill and base. */
export type WorkOrigin = {
  readonly kind: "work";
  readonly id: OriginId;
  readonly costId: string;
  readonly costType: CostType;
  readonly stage: Stage;
  /** Billable amount (positive). A provider's internal cost may differ — see
   * the `margin` derivation on absorption payments. */
  readonly amount: number;
  readonly estimated: boolean;
  /** Set for settlement-leg lawyer time (drives the dialog's sub-rows). */
  readonly settlementId?: string;
};

/** An active CFA — the fact that makes fee payments conditional. */
export type AgreementOrigin = {
  readonly kind: "agreement";
  readonly id: OriginId;
  readonly agreement: "lawFirmCfa" | "barristerCfa";
  /** Who contracted the provider (resolves who pays the fees). */
  readonly engagedBy: "party" | "lawFirm";
  /** Stages this CFA does not cover (provenance for coverage decisions). */
  readonly excludedStages?: ReadonlyArray<Stage>;
  /**
   * Disbursement types the (law-firm) CFA covers. An agreement fact the
   * projection needs beyond the cited edges: a covered barrister
   * disbursement makes a barristerFee recovery rule applicable to the
   * firm's base line even in outcomes where no fees actually passed on.
   */
  readonly includedDisbursements?: ReadonlyArray<DisbursementType>;
};

/** A quantum the court decided — awarded to or owed by the party. */
export type QuantumAwardOrigin = {
  readonly kind: "quantumAward";
  readonly id: OriginId;
  readonly quantumId: string;
  /** Issue title, resolved at emission (the wire renders names). */
  readonly quantumName: string;
  readonly direction: "awarded" | "toPay";
  /** Positive magnitude (the obligation's price). */
  readonly amount: number;
  /**
   * Liable opponents and their shares — provenance only. Shares may sum to
   * more than 1 under joint liability, which is why the judgment-debt
   * payment cites this origin instead of splitting per payer.
   */
  readonly liableOpponents?: ReadonlyArray<{
    readonly partyId: string;
    readonly share: number;
  }>;
};

/**
 * A costs order — the court ordering one side to reimburse the other's
 * costs. `direction: "awarded"` carries the applicability `rules`: a work
 * type WITH a rule but no recovery this outcome projects a present-but-zero
 * recovered cell (dialog "0"), a type without one projects none ("–").
 */
export type CostsOrderOrigin = {
  readonly kind: "costsOrder";
  readonly id: OriginId;
  readonly direction: "awarded" | "against";
  readonly rules: ReadonlyArray<{
    readonly costType: CostType;
    readonly percentage: number;
  }>;
  /** direction "against": the opponent the party must reimburse. */
  readonly recipientPartyId?: string;
};

/** An accepted settlement, with its waterfall decomposition as payload. */
export type SettlementOrigin = {
  readonly kind: "settlement";
  readonly id: OriginId;
  readonly settlementId: string;
  readonly stage: Stage;
  readonly counterpartyId: string;
  /** Display name, captured at emission (the wire renders names). */
  readonly counterpartyName: string;
  /**
   * How the gross settles against the party's positions — display slices,
   * signed from the party's perspective. Deliberately NOT payment items:
   * the gross moves once (the settlement payment); slicing it again would
   * double-count against costs and recoveries.
   */
  readonly breakdown: {
    readonly ofSettlement: number;
    readonly towardsPayer: number;
    readonly generic: number;
    readonly quantums: { readonly [quantumName: string]: number };
  };
};

/**
 * The enforcement plan this outcome executes — always the WORST plan from
 * the party's perspective (least recovered / most paid; ties broken by more
 * payments, then enumeration order). `value` is what the plan collects,
 * `totalCost` the plan's enforcement spend — the validator anchors for the
 * shortfall and the apportioned cost slices.
 */
export type EnforcementPlanOrigin = {
  readonly kind: "enforcementPlan";
  readonly id: OriginId;
  readonly value: number;
  readonly totalCost: number;
};

export type OriginItem =
  | WorkOrigin
  | AgreementOrigin
  | QuantumAwardOrigin
  | CostsOrderOrigin
  | SettlementOrigin
  | EnforcementPlanOrigin;

/** Type guard for one origin kind — usable directly in filter/find. */
export const isOriginOfKind =
  <K extends OriginItem["kind"]>(kind: K) =>
  (
    origin: OriginItem | undefined,
  ): origin is Extract<OriginItem, { kind: K }> =>
    origin?.kind === kind;

/** The origin of an agreement, when it participates in the outcome. */
export function agreementOrigin(
  origins: ReadonlyArray<OriginItem>,
  agreement: AgreementOrigin["agreement"],
): AgreementOrigin | undefined {
  return origins.find(
    (origin): origin is AgreementOrigin =>
      origin.kind === "agreement" && origin.agreement === agreement,
  );
}

/**
 * The awarded costs order — at most one exists per ledger (constant id, the
 * whole rule set in one origin); it carries the recovery applicability
 * regime.
 */
export function awardedCostsOrder(
  origins: ReadonlyArray<OriginItem>,
): CostsOrderOrigin | undefined {
  return origins.find(
    (origin): origin is CostsOrderOrigin =>
      origin.kind === "costsOrder" && origin.direction === "awarded",
  );
}

// ---- payment items ----------------------------------------------------------

/**
 * The four stored mechanisms — everything finer-grained derives from the
 * edge shape:
 *   - `absorption`: value consumed inside an entity — a provider's own work
 *     done at risk, or the firm's internal cost behind a billed fee. Payee
 *     is always `external` (nothing is transferred).
 *   - `billing`: consideration for work/disbursements as billed,
 *     unconditional. A billing citing fee PAYMENTS (instead of work) is a
 *     re-charge; a billing with an `enforcementApportionment` derivation is
 *     an enforcement cost slice.
 *   - `fee`: an agreement-rule-triggered transfer. Base / success / no-win
 *     derive from the derivation (none / successFee / lowFee).
 *   - `adjustment`: outcome-event transfers and reconciliations — settlement
 *     payments, judgment debts, recovery payments, enforcement shortfalls —
 *     told apart by the origin kinds they cite.
 */
export type Mechanism = "absorption" | "billing" | "fee" | "adjustment";

/**
 * Structured parameters of how a payment's amount was computed from its
 * citations. Facts, not prose: the validator re-checks the arithmetic and
 * the UI renders localized labels from the same numbers.
 */
export type Derivation =
  | { readonly rule: "margin"; readonly percentage: number }
  | {
      readonly rule: "successFee";
      /** Applied to the part of the cited base that is at risk. */
      readonly percentage: number;
      /**
       * The agreement's lowFee discount (0 without one): the share of the
       * base due as the no-win fee, hence not at risk. The item's amount is
       * `Σ cited payments × (1 − discount) × percentage`.
       */
      readonly discount: number;
    }
  | { readonly rule: "lowFee"; readonly discount: number }
  | {
      readonly rule: "recovery";
      /**
       * The rule percentage applied to the work's billable amount — the cap
       * before payer exposure. The item's amount is
       * `min(maxRecoverable, Σ payerShares.share × maxRecoverable)`: shares
       * may sum above 1 under joint apportionment, in which case the
       * recovery clamps at the cap (which is why this is ONE item per
       * obligation, not per payer — the clamp makes per-payer slices
       * non-additive).
       */
      readonly maxRecoverable: number;
      /** Liable opponents and their recovery shares, in obligation order. */
      readonly payerShares: ReadonlyArray<{
        readonly partyId: string;
        readonly share: number;
      }>;
      /**
       * Set on the recovery of the VAT on the work: a party that cannot
       * reclaim its VAT recovers it with the work, and the two are separate
       * items, each with its part of the cap.
       */
      readonly ofVat?: true;
    }
  | {
      readonly rule: "enforcementApportionment";
      readonly costType: CostType;
      /** The enforced opponent this slice is apportioned to; absent for the
       * plan's generic (payer-independent) slice. */
      readonly enforcedOpponentId?: string;
    }
  | {
      /**
       * Descriptive, not arithmetic: identifies the OPPONENT's cost an
       * adverse-costs payment reimburses. The opponent's work items are not
       * part of this party's ledger (no cross-party graph), so the identity
       * travels as data instead of an edge.
       */
      readonly rule: "adverseCost";
      readonly costType: CostType;
      readonly costStage: Stage;
    };

export type CausedBy = {
  /** The facts behind this payment. Non-empty for every payment — an item
   * without provenance is an orphan by schema. */
  readonly origins: ReadonlyArray<OriginId>;
  /** Upstream payments this one derives from (fees behind a re-charge, the
   * paid item a recovery reimburses, …). */
  readonly payments?: ReadonlyArray<PaymentId>;
  readonly derivation?: Derivation;
};

export type PaymentItem = {
  readonly id: PaymentId;
  readonly mechanism: Mechanism;
  readonly payer: EntityRef;
  readonly payee: EntityRef;
  /** Positive magnitude; direction is payer → payee. */
  readonly amount: number;
  readonly stage: Stage;
  /**
   * Set only where the legacy wire emits an estimated cost entry — it drives
   * the stage chain's one-stage lag for estimated spend.
   */
  readonly estimated?: boolean;
  /**
   * The VAT the payer pays on top of `amount` — to the tax authority, not to
   * the payee, whose income is `amount` alone. Set only on what a party that
   * cannot reclaim its VAT pays for work: its bills of work that carries VAT,
   * its CFA fees and the re-charges it is billed, each at
   * `context.vatRate` of the amount (see vat.ts).
   */
  readonly vat?: number;
  readonly causedBy: CausedBy;
};

// ---- container ----------------------------------------------------------------

export type LedgerItems = {
  /** Emission order (stable). */
  readonly origins: ReadonlyArray<OriginItem>;
  /**
   * Emission order; a cited payment always precedes its citer, so the DAG is
   * acyclic by array index.
   */
  readonly payments: ReadonlyArray<PaymentItem>;
  readonly context: {
    /** Opposing-party display names by id (projection renders names). */
    readonly partyNames: { readonly [partyId: string]: string };
    /** Reached stages in order — the stage-chain skeleton, not derivable
     * from the items alone. */
    readonly reached: ReadonlyArray<Stage>;
    /**
     * Recoveries the court awarded that have NO party-facing payment to
     * ride (modelling gap, e.g. incurred enforcement-stage work) — dropped
     * from the fold exactly like the legacy emitter dropped them, but
     * recorded so the gap is visible data and the validator's judgment
     * reconciliation still closes.
     */
    readonly droppedRecoveries: ReadonlyArray<{
      readonly costId: string;
      readonly costType: CostType;
      readonly amount: number;
    }>;
    /**
     * The rate of the VAT the party pays, absent when it reclaims its VAT —
     * the fact behind every payment's `vat`.
     */
    readonly vatRate?: number;
    /**
     * Whether the firm's own work is valued: where it is not, the items hold
     * no absorption of it and the firm's result is unknown (see FirmCosts).
     */
    readonly firmCosts: FirmCosts;
  };
};

// ---- deterministic ids --------------------------------------------------------

/**
 * The only construction path for item ids. Every input is stable per
 * computed outcome (costIds, settlementIds, quantumIds, partyIds, stages) —
 * recomputing a case yields identical ids, so diffs of persisted results
 * stay meaningful. Never derive ids from `randomId()`-bearing objects (e.g.
 * `RecoveryPayment.id` changes every recompute).
 */
export const ids = {
  work: (costId: string): OriginId => `o:work:${costId}`,
  agreement: (agreement: "lawFirmCfa" | "barristerCfa"): OriginId =>
    `o:agr:${agreement}`,
  quantum: (direction: "awarded" | "toPay", quantumId: string): OriginId =>
    `o:q:${direction}:${quantumId}`,
  costsOrderAwarded: (): OriginId => "o:co:awarded",
  costsOrderAgainst: (recipientPartyId: string): OriginId =>
    `o:co:against:${recipientPartyId}`,
  settlement: (settlementId: string): OriginId => `o:stl:${settlementId}`,
  enforcementPlan: (): OriginId => "o:enf:worst",

  /** The party-facing bill (or the firm's conventional payment) for a cost. */
  billing: (costId: string): PaymentId => `p:bill:${costId}`,
  /** The provider's own work behind a cost. */
  absorption: (costId: string): PaymentId => `p:abs:${costId}`,
  fee: (
    agreement: "lawFirmCfa" | "barristerCfa",
    feeType: string,
    stage: Stage,
  ): PaymentId => `p:fee:${agreement}:${feeType}:${stage}`,
  recharge: (stage: Stage): PaymentId => `p:rchg:${stage}`,
  settlementPayment: (settlementId: string): PaymentId =>
    `p:stl:${settlementId}`,
  quantumPayment: (
    direction: "awarded" | "toPay",
    quantumId: string,
  ): PaymentId => `p:q:${direction}:${quantumId}`,
  recoveryToPay: (recipientPartyId: string, index: number): PaymentId =>
    `p:rtp:${recipientPartyId}:${index}`,
  /** One per reimbursed work item — the clamp over joint payer shares makes
   * per-payer slices non-additive (see the recovery derivation). */
  recovery: (costId: string): PaymentId => `p:rec:${costId}`,
  /** The recovery of the VAT on a work item, beside its `recovery`. */
  vatRecovery: (costId: string): PaymentId => `p:rec:vat:${costId}`,
  shortfall: (): PaymentId => "p:shortfall",
  enforcementCost: (costType: CostType, payerId?: string): PaymentId =>
    `p:enf:${costType}:${payerId ?? "generic"}`,
} as const;
